import { useState, useRef, useEffect } from "react";
import { aiChatBot, downloadChatPdf } from "../api/post/post-chat";
import { getChatMessages } from "../api/get/get-chat";
import { getSecurityContextLabel } from "../components/buttons/1-chatbot/security-action-options";
import { useAuth } from "../context/contextAuth";

const useChat = () => {
  const { user, signIn, userId } = useAuth();

  /* ---------------- BASIC STATES ---------------- */
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [mount, setMount] = useState(false);

  /* ---------------- CHAT STATES ---------------- */
  const [chatMessages, setChatMessages] = useState([]);
  const [selectedVulnerability, setSelectedVulnerability] = useState(null);
  const [securityActionsAutoOpen, setSecurityActionsAutoOpen] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [isDownloading, setIsDownloading] = useState(null);

  /* ---------------- UI VISIBILITY STATES ---------------- */
  const [showChat, setShowChat] = useState(false);
  const [showCyberLaw, setShowCyberLaw] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  /* ---------------- RESET LOGIC ---------------- */
  const resetState = () => {
    setChatMessages([]);
    setSelectedVulnerability(null);
    setSecurityActionsAutoOpen(false);
    setPrompt("");
    setLoading(false);
    setMount(false);
    setInputDisabled(false);
    setIsDownloading(null);
    setShowChat(false);
    setShowCyberLaw(false);
  };

  const initData = async () => {
    if (!user) {
      resetState();
      return;
    }
    await fetchChat(userId);
  };

  useEffect(() => {
    if (showChat && signIn && userId && !loading) {
      fetchChat(userId);
    }
    // Loading is intentionally omitted. Direct finding actions open the chat and
    // immediately add a message; fetching history in that render would overwrite it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChat, signIn, userId]);

  const fetchChat = async (uid) => {
    try {
      const data = await getChatMessages(uid);
      const normalizedMessages = Array.isArray(data)
        ? data.flatMap(item => item.messages || [])
        : [];
      setChatMessages(normalizedMessages);
    } catch (err) {
      console.error("Error fetching chat history:", err);
      setChatMessages([]);
    }
  };

  const handleChat = async () => {
    if (!prompt.trim() || loading) return;
    const userPrompt = prompt;
    setPrompt("");
    setLoading(true);
    
    // Immediately append user message to layout
    setChatMessages(prev => [...prev, { type: "user", text: userPrompt }]);

    try {
      const response = await aiChatBot(userId, userPrompt);

      if (response?.error) {
        setChatMessages(prev => [
          ...prev, 
          { type: "bot", text: `⚠️ ${response.message}` }
        ]);
        setLoading(false);
        return;
      }

      // Append backend data packet instantly without pipeline loops
      setChatMessages(prev => [...prev, { type: "bot", text: response.reply }]);
    } catch (err) {
      setChatMessages(prev => [
        ...prev,
        { type: "bot", text: err.message || "❌ Server error. Please try again later." }
      ]);
    }
    setLoading(false);
  };

  const openVulnerabilityActions = (vulnerability) => {
    setSelectedVulnerability(vulnerability);
    setSecurityActionsAutoOpen(true);
    setPrompt("");
    setShowChat(true);
  };

  const runSecurityAction = async (vulnerability, actionId, actionLabel) => {
    if (!vulnerability || !actionId || loading) return;

    const contextLabel = getSecurityContextLabel(vulnerability);
    const visibleMessage = `${actionLabel}\n${contextLabel}`;
    setSelectedVulnerability(vulnerability);
    setSecurityActionsAutoOpen(false);
    setPrompt("");
    setShowChat(true);
    setLoading(true);
    setChatMessages((prev) => [
      ...prev,
      { type: "user", text: visibleMessage },
    ]);

    try {
      const response = await aiChatBot(userId, {
        securityActionId: actionId,
        vulnerability,
      });

      if (response?.error) {
        setChatMessages((prev) => [
          ...prev,
          { type: "bot", text: `⚠️ ${response.message}` },
        ]);
        return;
      }

      setChatMessages((prev) => [
        ...prev,
        { type: "bot", text: response.reply },
      ]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        { type: "bot", text: err.message || "❌ Server error. Please try again later." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSecurityAction = (actionId, actionLabel) =>
    runSecurityAction(selectedVulnerability, actionId, actionLabel);

  const handleSecurityActionFor = (vulnerability, actionId, actionLabel) =>
    runSecurityAction(vulnerability, actionId, actionLabel);

  const handleDownloadPdf = async (text, index) => {
    if (isDownloading !== null) return;  
    
    setIsDownloading(index);
    try {
      await downloadChatPdf(userId, text); 
    } catch (err) {
      alert("Download failed.");
    } finally {
      setIsDownloading(null);
    }
  };

  const checkPrompt = (text) => {
    if (!text.trim()) return;
    handleChat();
  };

  const handleClose = () => {
    setShowChat(false);
  };

  return {
    userId,
    user,
    mount,
    setMount,
    initData,
    resetState,
    chatMessages,
    selectedVulnerability,
    securityActionsAutoOpen,
    prompt,
    setPrompt,
    loading,
    setLoading,
    handleChat,
    openVulnerabilityActions,
    handleSecurityAction,
    handleSecurityActionFor,
    checkPrompt,
    messagesEndRef,
    textareaRef,
    showChat,
    setShowChat,
    handleClose,
    showCyberLaw,
    setShowCyberLaw,
    inputDisabled,
    setInputDisabled,
    handleDownloadPdf,
    isDownloading,
    setIsDownloading
  };
};

export default useChat;
